/**
 * toast notification module
 */
import Vue from 'vue'

/**
 * A fetch that was in flight when the session dropped still runs its catch, and
 * every store commits the failure here. The result lands on the login screen as
 * "Fetch Error / Failed to get <x> data" for a request the user can no longer
 * do anything about — the login screen is itself the feedback.
 *
 * The 401 interceptor clears accessToken synchronously, before the rejection
 * propagates, so its absence is a reliable marker that the session is gone. The
 * route check covers sitting on /auth/* with a token still present.
 *
 * Auth's own toasts (login failed, password reset, logout) call
 * Vue.prototype.$notification directly and never reach this mutation, so they
 * are unaffected.
 */
function isSignedOut() {
  if (!localStorage.getItem('accessToken')) return true
  try {
    // Lazy require to avoid circular import (store ⇄ router).
    // eslint-disable-next-line global-require
    const path = require('@/router').default.currentRoute.path
    return typeof path === 'string' && path.indexOf('/auth/') === 0
  } catch (e) {
    // Router not ready — fail open and show the toast.
    return false
  }
}

/**
 * Bulk loops (PDF route seeding, bulk update) toast once per failed request.
 * During an API restart that is N copies of the same "server is temporarily
 * unavailable" toast; one is enough, so an identical toast within this window
 * is dropped. Same idea as the 403 dedupe in the axios interceptor.
 */
const DUPLICATE_WINDOW_MS = 1500
let lastToastKey = ''
let lastToastAt = 0

export default {
  namespaced: true,
  state: {},
  mutations: {
    NEW(state, { type, message, description }) {
      if (type === 'error') {
        console.error(message)
      }

      if (isSignedOut()) return

      const key = `${type}|${message}|${description}`
      const now = Date.now()
      if (key === lastToastKey && now - lastToastAt < DUPLICATE_WINDOW_MS) return
      lastToastKey = key
      lastToastAt = now

      Vue.prototype.$notification[type]({
        message: message,
        description: description,
      })
    },
  },
}
